import { DecoratedCard } from '@src/components/DecoratedCard/DecoratedCard';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { FullCardInfo } from '@lub0v/santa-utils';
import {
  Button,
  ChevronRightIcon,
  CloseIcon,
  Confetti,
  HiddenFace,
  ParticipantFace,
} from '@src/components';
import { Landscape } from '@src/components/Landscape/Landscape';
import { getPortalRoot } from '@src/components/portalRoot';
import type { RevealFrame, RevealIdentity } from './useGifteeReveal';
import './GifteeRevealStage.css';

export function GifteeRevealStage({
  returnFocusRef,
  portraits,
  frame,
  result,
  busy,
  suspense,
  celebrating,
  onClose,
  onOpen,
}: {
  returnFocusRef: RefObject<HTMLButtonElement | null>;
  portraits: RevealIdentity[];
  frame: RevealFrame | null;
  result: FullCardInfo | null;
  busy: boolean;
  suspense: boolean;
  celebrating: boolean;
  onClose: () => void;
  onOpen: (fadeIn: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [opening, setOpening] = useState(false);
  const exitAnimation = useRef<Animation | null>(null);
  const person = frame?.person || result;
  const settled = !busy && Boolean(result);

  useEffect(() => {
    const dialog = dialogRef.current;
    const returnFocus = returnFocusRef.current;
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dialog?.showModal();
    return () => {
      exitAnimation.current?.cancel();
      dialog?.close();
      document.documentElement.style.overflow = overflow;
      requestAnimationFrame(() => {
        if (returnFocus?.isConnected && !document.querySelector('.giftee-stage[open]')) {
          returnFocus.focus({ preventScroll: true });
        }
      });
    };
  }, [returnFocusRef]);

  async function openCard() {
    if (exitAnimation.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await onOpen(false);
      return;
    }
    const dialog = dialogRef.current;
    const card = dialog?.querySelector<HTMLElement>('.giftee-stage__card');
    if (!dialog || !card) return;
    setOpening(true);
    // Keep the landscape opaque until the outgoing card has disappeared.
    const animation = card.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 500,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    exitAnimation.current = animation;
    try {
      await animation.finished;
    } catch {
      return; // Closing or unmounting cancels navigation as well as the fade.
    }
    if (!dialog.isConnected) return;
    // Persist the invisible end state before unmount cleanup cancels animations.
    animation.commitStyles();
    animation.cancel();
    if (!document.startViewTransition) {
      await onOpen(true);
      return;
    }
    // Capture the old landscape before React removes the top-layer dialog.
    // Commit the destination in the same update, with no exchange-page frame.
    const root = document.documentElement;
    root.classList.add('giftee-page-transition');
    const transition = document.startViewTransition(() => onOpen(false));
    const cleanup = () => root.classList.remove('giftee-page-transition');
    void transition.finished.then(cleanup, cleanup);
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className="giftee-stage"
      aria-labelledby={titleId}
      data-opening={opening || undefined}
      data-testid="giftee-reveal-stage"
      data-phase={settled ? 'revealed' : suspense ? 'suspense' : 'cycling'}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const buttons =
          event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="giftee-stage__landscape" aria-hidden="true">
        <Landscape appearance="soft" parallax={false} testId="giftee-reveal-landscape" />
      </div>
      <h2 id={titleId} className="giftee-stage__sr-only">
        {t('game.exchange.giftee')}
      </h2>
      <Confetti active={celebrating} variant="burst" testId="game-exchange-confetti" />
      <DecoratedCard className="person-card giftee-stage__card" variant={2} motif={false}>
        <Button
          className="giftee-stage__close"
          variant="ghost"
          icon={<CloseIcon />}
          aria-label={t('dialog.close')}
          onClick={onClose}
          testId="giftee-reveal-close"
        />
        <div className="giftee-stage__content">
          <div className="giftee-stage__portrait" aria-hidden="true">
            {portraits.map((portrait) => (
              <span
                key={portrait.id}
                className={`giftee-stage__glyph${person?.id === portrait.id ? ' giftee-stage__glyph--active' : ''}`}
              >
                <ParticipantFace card={portrait} size="round" />
              </span>
            ))}
            {!person && (
              <span className="giftee-stage__glyph giftee-stage__glyph--active">
                <HiddenFace size="round" />
              </span>
            )}
          </div>
          <div className="giftee-stage__name" aria-hidden={busy || undefined}>
            <span key={frame?.step ?? 'result'} className="giftee-stage__name-frame">
              {person?.username || t('game.exchange.revealing')}
            </span>
          </div>
          <div className="giftee-stage__action">
            {settled && !celebrating && (
              <Button
                variant="primary"
                size="large"
                icon={<ChevronRightIcon />}
                iconPosition="end"
                disabled={opening}
                onClick={() => void openCard()}
                testId="giftee-reveal-open-card"
              >
                {t('game.ward.openCard')}
              </Button>
            )}
          </div>
        </div>
      </DecoratedCard>
      <span className="giftee-stage__sr-only" role="status" aria-live="polite" aria-atomic="true">
        {settled && result
          ? t('game.exchange.revealedAnnouncement', { name: result.username })
          : t('game.exchange.revealing')}
      </span>
    </dialog>,
    getPortalRoot(),
  );
}
